// =============================================================================
// brandLogoGenerator — dwuetapowy generator logo (sygnetu) dla mapy.
//
//   Etap 1: Opus 4.8 pisze ANGIELSKI prompt obrazkowy + palete (prompt z DB,
//           edytowalny w /prompty) -> uzytkownik moze go recznie poprawic.
//   Etap 2: Recraft V4.1 Text-to-Vector zwraca PRAWDZIWY SVG (jedyna rodzina na
//           fal dajaca wektor zamiast rastra) -> uzytkownik wybiera jeden.
//
// Favicony NIE sa generowane przez AI — wylicza je lokalnie faviconBuilder
// z zapisanego SVG (zero dodatkowego kosztu, pelna spojnosc z logo).
//
// Wzorzec wywolania FAL 1:1 z authorPhotoGenerator: wlasny czysty payload,
// bez posrednictwa falaiService (ktory dokleja pola specyficzne dla flux-a).
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { getActiveWorkspace } from "@/lib/workspace";
import { loadPrompt } from "@/services/llm/promptLoader";
import { getLLMProvider, parseLLMJSON } from "@/services/llm";
import { compilePromptSafe } from "@/services/contentEngine/promptCompiler";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";
import { stripSvgBackground } from "@/lib/svgUtils";
import { normalizeHexColor } from "@/lib/faviconBuilder";
import type { BrandLogoSpec, RgbColor } from "@/types/brand";

/** Endpoint modelu na fal.run (przez proxy /api/fal-ai/).
 *  Warianty awaryjne: 'fal-ai/recraft/v4.1/pro/text-to-vector' (~$0.30) albo
 *  'fal-ai/recraft/v3/text-to-image' + style:'vector_illustration'. */
export const RECRAFT_VECTOR_ENDPOINT = "fal-ai/recraft/v4.1/text-to-vector";

export const MAX_LOGOS_PER_RUN = 4;

export interface BrandContext {
  mapId: string;
  serviceName: string;
  domain: string;
  siteDescription: string;
  macroStatement: string;
  audience: string;
  language: string;
}

// =============================================================================
// Kontekst — read-only, scoped do map_id
// =============================================================================

/** "https://www.example.pl/blog" -> "example.pl" */
export function domainFromUrl(url: string): string {
  const raw = (url || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
    return parsed.hostname.replace(/^www\./i, "");
  } catch {
    return raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0];
  }
}

export async function fetchBrandContext(mapId: string): Promise<BrandContext | null> {
  const { data: map } = await (supabase as any)
    .from("thematic_maps")
    .select("id, name, macro_statement, audience, language, website_id")
    .eq("id", mapId)
    .maybeSingle();
  if (!map) return null;

  let serviceName = map.name || "";
  let domain = "";
  let siteDescription = "";
  if (map.website_id) {
    const { data: site } = await (supabase as any)
      .from("websites")
      .select("name, url, organization_description")
      .eq("id", map.website_id)
      .maybeSingle();
    if (site) {
      serviceName = site.name || serviceName;
      domain = domainFromUrl(site.url || "");
      siteDescription = site.organization_description || "";
    }
  }

  return {
    mapId,
    serviceName,
    domain,
    siteDescription,
    macroStatement: map.macro_statement || "",
    audience: map.audience || "",
    language: map.language || "pl",
  };
}

// =============================================================================
// Etap 1 — spec logo (Opus 4.8)
// =============================================================================

/** Deterministyczny fallback — gdy brak promptu w DB, kompilacja padnie albo LLM zwroci smieci. */
export function buildFallbackLogoSpec(ctx: BrandContext): BrandLogoSpec {
  const letter = (ctx.domain || ctx.serviceName || "A").replace(/[^a-z]/gi, "").charAt(0).toUpperCase() || "A";
  return {
    prompt: [
      "Minimalist flat vector logo mark,",
      `abstract geometric letter ${letter} monogram built from two bold overlapping shapes,`,
      "simple geometric shapes, bold thick strokes, clean lines, flat design, solid colors,",
      "no gradients, no shadows, no 3D effects, deep navy blue and warm amber color scheme,",
      "isolated logo mark centered on plain solid white background, no background shapes, no frame, no border, no text",
    ].join(" "),
    colors: [
      { r: 20, g: 40, b: 80 },
      { r: 212, g: 175, b: 55 },
    ],
    theme_color: "#142850",
  };
}

/** RGB 0-255 z dowolnego smiecia (LLM lubi zwrocic string albo wartosc poza zakresem). */
function sanitizeColors(input: unknown): RgbColor[] {
  if (!Array.isArray(input)) return [];
  const clamp = (v: unknown) => Math.max(0, Math.min(255, Math.round(Number(v) || 0)));
  return input
    .filter((c) => c && typeof c === "object")
    .slice(0, 2)
    .map((c: any) => ({ r: clamp(c.r), g: clamp(c.g), b: clamp(c.b) }));
}

export interface BuildSpecResult {
  spec: BrandLogoSpec;
  source: "llm" | "fallback";
  costUsd?: number;
}

/** Etap 1: zbuduj spec logo. Nigdy nie rzuca — w najgorszym razie fallback. */
export async function buildBrandLogoSpec(mapId: string): Promise<BuildSpecResult> {
  const ctx = await fetchBrandContext(mapId);
  if (!ctx) throw new Error("Nie znaleziono mapy");

  const fallback = buildFallbackLogoSpec(ctx);

  const dbPrompt = await loadPrompt("brand_logo_prompt" as any);
  if (!dbPrompt) {
    logError("BRAND-LOGO", "Brak aktywnego promptu 'brand_logo_prompt' — fallback");
    return { spec: fallback, source: "fallback" };
  }

  const compiled = compilePromptSafe(
    dbPrompt.template,
    {
      service_name: ctx.serviceName,
      domain: ctx.domain,
      site_description: ctx.siteDescription,
      macro_statement: ctx.macroStatement,
      audience: ctx.audience,
      language: ctx.language,
    },
    "brand_logo_prompt",
    "__FALLBACK__",
  );
  if (compiled === "__FALLBACK__") {
    logError("BRAND-LOGO", "Kompilacja promptu nie powiodla sie (unresolved vars) — fallback");
    return { spec: fallback, source: "fallback" };
  }

  try {
    const provider = getLLMProvider((dbPrompt.provider || "claude") as "openai" | "claude");
    const resp = await provider.call(compiled, {
      model: dbPrompt.modelId || "claude-opus-5",
      // NULL w DB = nie narzucamy probkowania. Opus 4.7/4.8 i tak odrzuca temperature
      // (400) — claudeProvider.supportsSampling() zdejmuje ja z body.
      temperature: dbPrompt.temperature ?? undefined,
      step: "brand_logo_prompt",
      mapId,
    });

    const parsed = parseLLMJSON<{ prompt?: string; colors?: unknown; theme_color?: string }>(resp.text);
    const prompt = (parsed?.prompt || "").trim();
    if (prompt.length < 30) {
      logError("BRAND-LOGO", `LLM zwrocil za krotki prompt (${prompt.length} zn.) — fallback`);
      return { spec: fallback, source: "fallback" };
    }

    const colors = sanitizeColors(parsed?.colors);
    logPublic("BRAND-LOGO", `Spec logo gotowy (${prompt.length} zn., $${(resp.costUsd ?? 0).toFixed(4)})`);
    return {
      spec: {
        prompt,
        colors: colors.length ? colors : fallback.colors,
        theme_color: normalizeHexColor(parsed?.theme_color),
      },
      source: "llm",
      costUsd: resp.costUsd,
    };
  } catch (e) {
    logError("BRAND-LOGO", "LLM padl przy budowaniu spec logo — fallback", e);
    return { spec: fallback, source: "fallback" };
  }
}

// =============================================================================
// Etap 2 — SVG z Recraft V4.1 Vector
// =============================================================================

/** Czysty payload Recraft — tylko udokumentowane pola. */
export function buildRecraftPayload(prompt: string, colors: RgbColor[]) {
  return {
    prompt,
    image_size: "square_hd",
    colors,
    background_color: { r: 255, g: 255, b: 255 },
  };
}

async function generateOneLogo(prompt: string, colors: RgbColor[]): Promise<string> {
  const resp = await fetch(`/api/fal-ai/${RECRAFT_VECTOR_ENDPOINT}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // nginx /api/fal-ai/ nadpisuje Authorization kluczem per workspace
      Authorization: "Key ",
      "X-Workspace": getActiveWorkspace(),
    },
    body: JSON.stringify(buildRecraftPayload(prompt, colors)),
  });

  if (!resp.ok) {
    const body = await resp.text();
    throw new Error(`FAL ${resp.status}: ${body.slice(0, 200)}`);
  }

  const json = await resp.json();
  const url = json?.images?.[0]?.url;
  if (!url || typeof url !== "string") throw new Error("Recraft nie zwrocil pliku SVG");

  // Potrzebujemy TRESCI svg (rasteryzacja z <img src=https://...> zabrudzilaby canvas).
  const svgResp = await fetch(url);
  if (!svgResp.ok) throw new Error(`Nie udalo sie pobrac SVG (${svgResp.status})`);
  const svg = await svgResp.text();
  if (!svg.includes("<svg")) throw new Error("Odpowiedz nie jest plikiem SVG");

  return stripSvgBackground(svg);
}

export interface GenerateLogosResult {
  ok: number;
  failed: number;
  errors: string[];
}

/**
 * Odpala N NIEZALEZNYCH requestow rownolegle — kazdy daje inny wariant symbolu,
 * jeden blad nie zabija reszty, a gotowe SVG leca od razu do onSvg().
 */
export async function generateLogoSvgs(opts: {
  prompt: string;
  colors: RgbColor[];
  count: number;
  onSvg?: (svg: string) => void;
}): Promise<GenerateLogosResult> {
  const count = Math.max(1, Math.min(MAX_LOGOS_PER_RUN, opts.count));
  const errors: string[] = [];
  let ok = 0;
  let failed = 0;

  const results = await Promise.allSettled(
    Array.from({ length: count }, () =>
      generateOneLogo(opts.prompt, opts.colors).then((svg) => {
        opts.onSvg?.(svg);
        return svg;
      }),
    ),
  );

  for (const r of results) {
    if (r.status === "fulfilled") ok++;
    else {
      failed++;
      const msg = r.reason instanceof Error ? r.reason.message : String(r.reason);
      if (!errors.includes(msg)) errors.push(msg);
    }
  }

  logPublic("BRAND-LOGO", `Wygenerowano ${ok}/${count} propozycji logo (bledy: ${failed})`);
  return { ok, failed, errors };
}
